import { isCurrentDay } from '@epicuro-next/utilities';
import { createSelector } from '@ngrx/store';
import { OrderType } from 'graphql-api/gql-api-interfaces';

import { DeliveryItems } from '../../../models';
import { getNowDateContainer } from '../../../shared/now-date-container/get-now-date-container';
import { isTrueDemandInventory } from '../../../shared/true-demand-inventory';
import { unpackNowDate } from '../../../shared/unpack-now-date';
import {
  selectBackOrderEnabledRule,
  selectSkipInventoryCheckEnabled,
} from '../../config-strip-configuration.selectors';
import {
  selectCurrentlyActiveShopDeliveryItems,
  selectDeliveryItemsAcrossShops,
} from '../../config-strip.selectors';
import {
  selectPrepackEntities,
  selectSizeEntities,
} from '../../data/config-strip.data.selectors';
import { mergeSplitQuantitiesByDate } from '../../shared/merge-split-quantities';
import { splitQuantities } from '../../shared/split-quantities';
import { selectAdvancedMdo } from '../../ui/config-strip.ui.selectors';

import { calculatePrepacks } from './calculate-prepacks';

export const selectNowTotals = createSelector(
  selectCurrentlyActiveShopDeliveryItems,
  selectDeliveryItemsAcrossShops,
  selectSizeEntities,
  selectPrepackEntities,
  selectBackOrderEnabledRule,
  selectAdvancedMdo,
  selectSkipInventoryCheckEnabled,
  (
    deliveryItems,
    allItems,
    sizes,
    prepacks,
    isBackOrderEnabled,
    isAdvancedMdo,
    skipInventoryCheckEnabled,
  ) => {
    let nowDeliveryItems: DeliveryItems | undefined =
      deliveryItems[OrderType.Now];

    if (isAdvancedMdo) {
      nowDeliveryItems = allItems[OrderType.Now];
    }

    if (!isAdvancedMdo && !nowDeliveryItems) {
      nowDeliveryItems = Object.values(deliveryItems).find(
        (item) => item.orderType === OrderType.Now,
      );
    }

    const nowDummyQuantity = {
      requestedDeliveryDate: getNowDateContainer(),
      requestedQuantity: 0,
    };
    if (!nowDeliveryItems || !nowDeliveryItems.items) {
      return [nowDummyQuantity];
    }
    const productIds = Object.keys(nowDeliveryItems.items);

    const split = productIds
      .flatMap((productId) =>
        splitQuantities(
          productId,
          nowDeliveryItems as DeliveryItems,
          OrderType.Now,
          sizes,
          prepacks,
          isBackOrderEnabled,
          skipInventoryCheckEnabled,
        ),
      )
      // do not show true demand in delivery periods
      .filter((item) => !isTrueDemandInventory(item.inventoryId as string))
      .map((item) => ({
        ...item,
        requestedDeliveryDate: unpackNowDate(item.requestedDeliveryDate),
      }));
    const merged = mergeSplitQuantitiesByDate(split);
    if (!merged.length) {
      return [nowDummyQuantity];
    }
    const hasToday = merged.some((quantity) =>
      isCurrentDay(new Date(quantity.requestedDeliveryDate)),
    );
    if (!hasToday) {
      merged.splice(0, 0, nowDummyQuantity);
    }

    const [now, ...backOrders] = merged;
    return [
      {
        ...now,
        requestedQuantity: calculatePrepacks(
          now.requestedQuantity,
          nowDeliveryItems,
          prepacks,
        ),
      },
      ...backOrders,
    ];
  },
);
